import { useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Slider } from "../ui/slider";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { MultiImageUpload } from "../ui/image-upload";
import { FramedImage } from "../ui/FramedImage";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { toast } from "sonner";
import { Plus, Edit, Trash2, X, ImageIcon } from "lucide-react";
import { API } from "../../apiConfig";


const ProductsTab = ({ products, token, onRefresh }) => {
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    title: "",
    description: "",
    story: "",
    base_price: "",
    category: "limited_edition",
    images: [],
    stock: 100,
    is_limited_edition: true,
    is_featured: false,
    is_available: true,
    status: "active",
    has_variants: true,
    wholesale_min_qty: 1,
    medium: "",
    dimensions: "",
    artist_name: "Colin Isaacs",
    image_scale: 100, // Image display size percentage (50-150)
    variants: [
      { name: "Small", size: "A4 (210×297mm)", price: 199, base_price: 199, total_supply: 100, available_for_sale: 25, sold_count: 0, stock: 25, wholesale_min_qty: 4 },
      { name: "Medium", size: "A3 (297×420mm)", price: 399, base_price: 399, total_supply: 75, available_for_sale: 25, sold_count: 0, stock: 25, wholesale_min_qty: 3 },
      { name: "Large", size: "A2 (420×594mm)", price: 699, base_price: 699, total_supply: 50, available_for_sale: 25, sold_count: 0, stock: 25, wholesale_min_qty: 2 }
    ]
  });

  const headers = { Authorization: `Bearer ${token}` };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(price || 0);
  };

  const openProductDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setProductForm({
        title: product.title || "",
        description: product.description || "",
        story: product.story || "",
        base_price: product.base_price || product.price || "",
        category: product.category || "limited_edition",
        images: product.images || [],
        stock: product.stock || 100,
        is_limited_edition: product.is_limited_edition ?? true,
        is_featured: product.is_featured ?? false,
        is_available: product.is_available ?? true,
        status: product.status || "active",
        has_variants: product.has_variants ?? true,
        wholesale_min_qty: product.wholesale_min_qty || 1,
        medium: product.medium || "",
        dimensions: product.dimensions || "",
        artist_name: product.artist_name || "Colin Isaacs",
        image_scale: product.image_scale || 100,
        variants: product.variants || []
      });
    } else {
      setEditingProduct(null);
      setProductForm({
        title: "",
        description: "",
        story: "",
        base_price: "",
        category: "limited_edition",
        images: [],
        stock: 100,
        is_limited_edition: true,
        is_featured: false,
        is_available: true,
        status: "active",
        has_variants: true,
        wholesale_min_qty: 1,
        medium: "",
        dimensions: "",
        artist_name: "Colin Isaacs",
        image_scale: 100,
        variants: [
          { name: "Small", size: "A4 (210×297mm)", price: 199, base_price: 199, total_supply: 100, available_for_sale: 25, sold_count: 0, stock: 25, wholesale_min_qty: 4 },
          { name: "Medium", size: "A3 (297×420mm)", price: 399, base_price: 399, total_supply: 75, available_for_sale: 25, sold_count: 0, stock: 25, wholesale_min_qty: 3 },
          { name: "Large", size: "A2 (420×594mm)", price: 699, base_price: 699, total_supply: 50, available_for_sale: 25, sold_count: 0, stock: 25, wholesale_min_qty: 2 }
        ]
      });
    }
    setProductDialogOpen(true);
  };

  const saveProduct = async () => {
    try {
      const productData = {
        ...productForm,
        price: parseFloat(productForm.base_price) || 0,
        base_price: parseFloat(productForm.base_price) || 0,
        artist_id: "artist_colinisaacs",
        is_available: productForm.status === "active"
      };

      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.product_id}`, productData, { headers });
        toast.success("Product updated successfully");
      } else {
        await axios.post(`${API}/products`, productData, { headers });
        toast.success("Product created successfully");
      }
      
      setProductDialogOpen(false);
      onRefresh();
    } catch (error) {
      console.error("Error saving product:", error);
      toast.error("Failed to save product");
    }
  };

  const deleteProduct = async (productId) => {
    if (!confirm("Are you sure you want to delete this product?")) return;
    
    try {
      await axios.delete(`${API}/admin/products/${productId}`, { headers });
      toast.success("Product deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const updateVariant = (index, field, value) => {
    const newVariants = [...productForm.variants];
    newVariants[index] = { ...newVariants[index], [field]: value };
    if (field === "price") {
      newVariants[index].base_price = value;
    }
    setProductForm({ ...productForm, variants: newVariants });
  };

  const addVariant = () => {
    setProductForm({
      ...productForm,
      variants: [...productForm.variants, { 
        name: "New Size", 
        size: "", 
        price: 0, 
        base_price: 0, 
        total_supply: 50,
        available_for_sale: 25,
        sold_count: 0,
        stock: 25, 
        wholesale_min_qty: 1 
      }]
    });
  };

  const removeVariant = (index) => {
    setProductForm({
      ...productForm,
      variants: productForm.variants.filter((_, i) => i !== index)
    });
  };

  return (
    <>
      <Card className="rounded-2xl" data-testid="products-tab">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Product Management</CardTitle>
          <Button onClick={() => openProductDialog()} className="rounded-full" data-testid="add-product-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Product
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Stock</TableHead>
                <TableHead>Variants</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((product) => (
                <TableRow key={product.product_id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <img
                        src={product.images?.[0] || "https://via.placeholder.com/40"}
                        alt={product.title}
                        className="w-10 h-10 rounded object-cover"
                      />
                      <div>
                        <p className="font-medium line-clamp-1">{product.title}</p>
                        {product.is_featured && <Badge variant="outline" className="text-xs">Featured</Badge>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="capitalize">{product.category?.replace("_", " ")}</TableCell>
                  <TableCell>{formatPrice(product.price)}</TableCell>
                  <TableCell>{product.stock || product.quantity || 0}</TableCell>
                  <TableCell>
                    {product.has_variants ? (
                      <span className="text-sm">{product.variants?.length || 0} sizes</span>
                    ) : "-"}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const status = product.status || (product.is_available !== false ? "active" : "inactive");
                      const statusConfig = {
                        active: { color: "bg-green-500", text: "Active", textColor: "text-green-700" },
                        draft: { color: "bg-orange-500", text: "Draft", textColor: "text-orange-700" },
                        inactive: { color: "bg-gray-400", text: "Inactive", textColor: "text-gray-600" }
                      };
                      const config = statusConfig[status] || statusConfig.active;
                      return (
                        <div className="flex items-center gap-2">
                          <span className={`w-2.5 h-2.5 rounded-full ${config.color}`} />
                          <span className={`text-sm font-medium ${config.textColor}`}>{config.text}</span>
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="icon" onClick={() => openProductDialog(product)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => deleteProduct(product.product_id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Product Dialog */}
      <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProduct ? "Edit Product" : "Add New Product"}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Title</Label>
                <Input
                  value={productForm.title}
                  onChange={(e) => setProductForm({ ...productForm, title: e.target.value })}
                  placeholder="Product title"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={productForm.category} onValueChange={(v) => setProductForm({ ...productForm, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="original">Original</SelectItem>
                    <SelectItem value="limited_edition">Limited Edition</SelectItem>
                    <SelectItem value="print">Print</SelectItem>
                    <SelectItem value="merchandise">Merchandise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Base Price (AUD)</Label>
                <Input
                  type="number"
                  value={productForm.base_price}
                  onChange={(e) => setProductForm({ ...productForm, base_price: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Medium</Label>
                <Input
                  value={productForm.medium}
                  onChange={(e) => setProductForm({ ...productForm, medium: e.target.value })}
                  placeholder="e.g., Acrylic on Canvas"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Dimensions</Label>
                <Input
                  value={productForm.dimensions}
                  onChange={(e) => setProductForm({ ...productForm, dimensions: e.target.value })}
                  placeholder="e.g., 60 x 90 cm"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Artist Name</Label>
                <Input
                  value={productForm.artist_name}
                  onChange={(e) => setProductForm({ ...productForm, artist_name: e.target.value })}
                  placeholder="Colin Isaacs"
                />
              </div>
              
              <div className="col-span-2 space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={productForm.description}
                  onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                  placeholder="Product description"
                  rows={3}
                />
              </div>
              
              <div className="col-span-2 space-y-2">
                <Label>Story / Cultural Significance</Label>
                <Textarea
                  value={productForm.story}
                  onChange={(e) => setProductForm({ ...productForm, story: e.target.value })}
                  placeholder="Cultural story and significance"
                  rows={3}
                />
              </div>
              
              <div className="col-span-2 space-y-2">
                <Label>Images</Label>
                <MultiImageUpload
                  images={productForm.images}
                  onChange={(images) => setProductForm({ ...productForm, images })}
                  maxImages={6}
                />
              </div>
              
              {/* Image Display Size Control */}
              {productForm.images?.length > 0 && (
                <div className="col-span-2 space-y-4 p-4 bg-muted/30 rounded-lg">
                  <div className="flex items-center gap-2">
                    <ImageIcon className="w-4 h-4 text-muted-foreground" />
                    <Label>Image Display Size</Label>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-6">
                    {/* Preview */}
                    <div className="aspect-square bg-muted/50 rounded-lg flex items-center justify-center p-4 overflow-hidden">
                      <div 
                        style={{ 
                          transform: `scale(${productForm.image_scale / 100})`,
                          transformOrigin: 'center',
                          transition: 'transform 0.2s ease'
                        }}
                      >
                        <FramedImage
                          src={productForm.images[0]}
                          alt="Preview"
                          frameScale={0.4}
                          className="max-w-full max-h-full"
                        />
                      </div>
                    </div>
                    
                    {/* Controls */}
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between mb-2">
                          <span className="text-sm text-muted-foreground">Scale</span>
                          <span className="text-sm font-medium">{productForm.image_scale}%</span>
                        </div>
                        <Slider
                          value={[productForm.image_scale]}
                          onValueChange={([v]) => setProductForm({ ...productForm, image_scale: v })}
                          min={50}
                          max={150}
                          step={5}
                        />
                        <div className="flex justify-between mt-1 text-xs text-muted-foreground">
                          <span>Smaller</span>
                          <span>Larger</span>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setProductForm({ ...productForm, image_scale: 80 })}
                          className={productForm.image_scale === 80 ? "ring-2 ring-primary" : ""}
                        >
                          Small
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setProductForm({ ...productForm, image_scale: 100 })}
                          className={productForm.image_scale === 100 ? "ring-2 ring-primary" : ""}
                        >
                          Default
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setProductForm({ ...productForm, image_scale: 120 })}
                          className={productForm.image_scale === 120 ? "ring-2 ring-primary" : ""}
                        >
                          Large
                        </Button>
                      </div>
                      
                      <p className="text-xs text-muted-foreground">
                        Adjust how large the artwork appears within its frame. Smaller values show more mat, larger values fill more of the frame.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex items-center justify-between">
                <Label>Limited Edition</Label>
                <Switch
                  checked={productForm.is_limited_edition}
                  onCheckedChange={(v) => setProductForm({ ...productForm, is_limited_edition: v })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label>Featured</Label>
                <Switch
                  checked={productForm.is_featured}
                  onCheckedChange={(v) => setProductForm({ ...productForm, is_featured: v })}
                />
              </div>
              
              <div className="col-span-2 space-y-2">
                <Label>Product Status</Label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setProductForm({ ...productForm, status: "active" })}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                      productForm.status === "active" 
                        ? "border-green-500 bg-green-50 text-green-700" 
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <span className={`w-3 h-3 rounded-full ${productForm.status === "active" ? "bg-green-500" : "bg-gray-300"}`} />
                    <span className="font-medium">Active</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setProductForm({ ...productForm, status: "draft" })}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                      productForm.status === "draft" 
                        ? "border-orange-500 bg-orange-50 text-orange-700" 
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <span className={`w-3 h-3 rounded-full ${productForm.status === "draft" ? "bg-orange-500" : "bg-gray-300"}`} />
                    <span className="font-medium">Draft</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setProductForm({ ...productForm, status: "inactive" })}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                      productForm.status === "inactive" 
                        ? "border-gray-500 bg-gray-100 text-gray-700" 
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <span className={`w-3 h-3 rounded-full ${productForm.status === "inactive" ? "bg-gray-500" : "bg-gray-300"}`} />
                    <span className="font-medium">Inactive</span>
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Active products are visible to customers. Draft products are saved but hidden. Inactive products are disabled.
                </p>
              </div>
              
              <div className="flex items-center justify-between">
                <Label>Has Variants (Sizes)</Label>
                <Switch
                  checked={productForm.has_variants}
                  onCheckedChange={(v) => setProductForm({ ...productForm, has_variants: v })}
                />
              </div>
              
              {!productForm.has_variants && (
                <div className="space-y-2">
                  <Label>Stock</Label>
                  <Input
                    type="number"
                    value={productForm.stock}
                    onChange={(e) => setProductForm({ ...productForm, stock: parseInt(e.target.value) || 0 })}
                  />
                </div>
              )}
            </div>
            
            {/* Variants Section */}
            {productForm.has_variants && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-semibold">Limited Edition Sizes</Label>
                    <p className="text-xs text-muted-foreground mt-1">Each size is its own limited edition license</p>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={addVariant}>
                    <Plus className="h-4 w-4 mr-1" /> Add Size
                  </Button>
                </div>
                
                <div className="space-y-4">
                  {productForm.variants.map((variant, idx) => (
                    <Card key={idx} className="border-2">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">Edition {idx + 1}</Badge>
                            <span className="font-medium">{variant.name || 'Unnamed'} - {variant.size || 'No size'}</span>
                          </div>
                          <Button variant="ghost" size="icon" onClick={() => removeVariant(idx)}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        {/* Basic Info Row */}
                        <div className="grid grid-cols-3 gap-3 mb-4">
                          <div className="space-y-1">
                            <Label className="text-xs">Size Name</Label>
                            <Input
                              placeholder="e.g. Small"
                              value={variant.name}
                              onChange={(e) => updateVariant(idx, "name", e.target.value)}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Dimensions</Label>
                            <Input
                              placeholder="e.g. 400x300mm"
                              value={variant.size}
                              onChange={(e) => updateVariant(idx, "size", e.target.value)}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Price (AUD)</Label>
                            <Input
                              type="number"
                              placeholder="199"
                              value={variant.price}
                              onChange={(e) => updateVariant(idx, "price", parseFloat(e.target.value) || 0)}
                            />
                          </div>
                        </div>
                        
                        {/* Edition Management Row */}
                        <div className="bg-muted/50 rounded-lg p-3">
                          <Label className="text-xs font-semibold text-primary mb-2 block">Edition Management</Label>
                          <div className="grid grid-cols-4 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">Total Supply</Label>
                              <Input
                                type="number"
                                placeholder="100"
                                value={variant.total_supply || variant.original_stock || variant.stock || 0}
                                onChange={(e) => {
                                  const total = parseInt(e.target.value) || 0;
                                  updateVariant(idx, "total_supply", total);
                                  updateVariant(idx, "original_stock", total);
                                }}
                              />
                              <p className="text-[10px] text-muted-foreground">Total edition size</p>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">Released for Sale</Label>
                              <Input
                                type="number"
                                placeholder="25"
                                value={variant.available_for_sale || variant.stock || 0}
                                onChange={(e) => {
                                  const available = parseInt(e.target.value) || 0;
                                  updateVariant(idx, "available_for_sale", available);
                                  updateVariant(idx, "stock", available - (variant.sold_count || 0));
                                }}
                              />
                              <p className="text-[10px] text-muted-foreground">How many released</p>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">Sold</Label>
                              <Input
                                type="number"
                                placeholder="0"
                                value={variant.sold_count || 0}
                                onChange={(e) => {
                                  const sold = parseInt(e.target.value) || 0;
                                  updateVariant(idx, "sold_count", sold);
                                  updateVariant(idx, "stock", (variant.available_for_sale || variant.stock || 0) - sold);
                                }}
                              />
                              <p className="text-[10px] text-muted-foreground">Units sold</p>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">In Stock</Label>
                              <Input
                                type="number"
                                value={variant.stock || 0}
                                disabled
                                className="bg-muted"
                              />
                              <p className="text-[10px] text-muted-foreground">Auto-calculated</p>
                            </div>
                          </div>
                          
                          {/* Progress Bar */}
                          <div className="mt-3">
                            <div className="flex justify-between text-xs mb-1">
                              <span className="text-muted-foreground">
                                {variant.sold_count || 0} sold of {variant.available_for_sale || variant.stock || 0} released
                              </span>
                              <span className="text-muted-foreground">
                                {(variant.total_supply || variant.original_stock || 0) - (variant.available_for_sale || variant.stock || 0)} held back
                              </span>
                            </div>
                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-primary transition-all"
                                style={{ 
                                  width: `${((variant.sold_count || 0) / (variant.available_for_sale || variant.stock || 1)) * 100}%` 
                                }}
                              />
                            </div>
                          </div>
                        </div>
                        
                        {/* Wholesale Row */}
                        <div className="grid grid-cols-3 gap-3 mt-3">
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Min Wholesale Qty</Label>
                            <Input
                              type="number"
                              placeholder="1"
                              value={variant.wholesale_min_qty || 1}
                              onChange={(e) => updateVariant(idx, "wholesale_min_qty", parseInt(e.target.value) || 1)}
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setProductDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveProduct}>{editingProduct ? "Update" : "Create"} Product</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProductsTab;
